import React from "react";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Tag, ExternalLink } from "lucide-react";
import { useShoppingDeals, DealProps } from "@/hooks/use-shopping-deals";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";

const DealCard = ({
  image,
  title,
  originalPrice,
  salePrice,
  discount,
  store,
  link,
  delay,
}: DealProps) => (
  <div
    className="rounded-xl overflow-hidden glass-card hover-lift animate-fade-in opacity-0"
    style={{ animationDelay: `${delay}s` }}
  >
    <div className="relative">
      <div className="aspect-[4/3] overflow-hidden">
        <img src={image} alt={title} className="w-full h-full object-cover" />
      </div>
      <div className="absolute top-3 right-3 py-1 px-2 bg-gold text-navy text-xs font-bold rounded-md flex items-center">
        <Tag className="h-3 w-3 mr-1" />
        {discount} OFF
      </div>
    </div>
    <div className="p-5">
      <div className="text-xs text-navy/60 font-medium mb-2">{store}</div>
      <h3 className="text-navy font-medium mb-3 line-clamp-2">{title}</h3>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-navy font-bold">{salePrice}</span>
        <span className="text-navy/60 text-sm line-through">
          {originalPrice}
        </span>
      </div>
      <Button className="w-full bg-navy hover:bg-navy-light justify-center group">
        <ShoppingBag className="h-4 w-4 mr-2" />
        <span>Shop Now</span>
        <ExternalLink className="h-3 w-3 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
      </Button>
    </div>
  </div>
);

const DealCardSkeleton = () => (
  <div className="rounded-xl overflow-hidden border border-gray-200">
    <div className="relative">
      <Skeleton className="aspect-[4/3] w-full" />
    </div>
    <div className="p-5 space-y-3">
      <Skeleton className="h-3 w-16" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
      <div className="flex items-center gap-2 py-2">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-3 w-16" />
      </div>
      <Skeleton className="h-9 w-full" />
    </div>
  </div>
);

const ShoppingDeals = () => {
  const { deals, isLoading, error } = useShoppingDeals();

  return (
    <section className="py-16 md:py-24 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6 w-full max-w-full">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-navy">
            Exclusive Shopping Deals
          </h2>
          <p className="mt-4 text-lg text-navy/70 max-w-2xl mx-auto">
            Curated offers from our favorite brands to help you shop smarter and
            save more
          </p>
        </div>

        {error && (
          <div className="bg-red-100 text-red-700 p-4 mx-auto max-w-4xl mb-8 rounded-md text-center">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {isLoading
            ? // Hiển thị skeleton loader khi đang tải
              [...Array(4)].map((_, index) => <DealCardSkeleton key={index} />)
            : // Hiển thị deals khi đã tải xong
              deals.map((deal, index) => <DealCard key={index} {...deal} />)}
        </div>

        <div className="text-center mt-12">
          <Link to="/deals">
            <Button
              variant="outline"
              className="border-navy text-navy hover:bg-navy hover:text-white"
            >
              View All Deals
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ShoppingDeals;
